import React, {ReactElement, useMemo} from 'react';
import {Typography} from '@customink/pigment-react';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useStoreSlug} from '../../hooks';
import './index.scss';
import {
  Order,
  OrderConfirmationResponse,
  OrderDetails
} from '../../models/orderConfirmation';

const DETAIL = {
  SIZE: 'size',
  COLOR: 'color'
};

const ListPlaceholder: React.FC = () => {
  const mock = [1, 2];
  return (
    <>
      {mock.map(
        (row: number): ReactElement => (
          <div className="orderListPlaceholder" key={row}>
            <Skeleton className="skeletonBox" variant="rectangular" />
            <div className="skeletonInfo">
              <Skeleton className="skeletonText" variant="text" />
              <Skeleton className="skeletonText" variant="text" />
            </div>
          </div>
        )
      )}
    </>
  );
};

const tableHeader = (data: OrderConfirmationResponse) =>
  useMemo(() => {
    const {order} = data;
    if (!data || !order) return <></>;
    const header = data ? Object.keys(order[0])?.map(item => item) : [];
    return (
      <TableHead>
        <TableRow>
          {header?.map((headCell: string) => {
            return (
              <TableCell key={headCell} className="headerCell">
                <Typography variant="subHeading">{headCell}</Typography>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }, [data]);

const tableBodyDetails = (item: Order) => {
  if (!item) return <></>;
  return (
    <ul>
      {Object.keys(item.details).map((ele: keyof OrderDetails) => {
        return (
          <li key={ele}>
            <Typography
              variant="subHeading"
              className={ele === DETAIL.SIZE ? DETAIL.SIZE : DETAIL.COLOR}>
              {ele}:{' '}
              {ele === 'size'
                ? item.details[ele].map((size: string, sizeIndex: number) => {
                    return `${size}${
                      item.details[ele].length - 1 === sizeIndex ? '' : ', '
                    }`;
                  })
                : item.details[ele]}
            </Typography>
          </li>
        );
      })}
    </ul>
  );
};

const tableBody = (item: Order) =>
  useMemo(() => {
    if (!item) return <></>;
    return (
      <TableBody className="tableBody">
        <TableRow>
          <TableCell className="productInfo">
            <img src={item.name.image} alt={item.name.productName} />
            <Typography variant="subHeading" className="productInfo-name">
              {item.name.productName}
            </Typography>
          </TableCell>
          <TableCell className="productDetails" align="left">
            {tableBodyDetails(item)}
          </TableCell>
          <TableCell className="productQuantity" align="left">
            <Typography variant="subHeading">
              <strong>{item.quantity}</strong>
            </Typography>
          </TableCell>
          <TableCell className="productStatus" align="left">
            <Typography className="productStatus-message" variant="subHeading">
              {item.status.statusMessage}
            </Typography>
            <br />
            <Typography className="delivery" variant="subHeading">
              <span className="title">Estimated Delivery: </span>
              <span className="date">{item.status.statusDate}</span>
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }, [item]);

const OrderConfirmationList: React.FC<OrderConfirmationResponse> = (
  data: OrderConfirmationResponse
): ReactElement => {
  const {order} = data;
  if (!data || !order) {
    return <ListPlaceholder />;
  }
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));
  const storeSlug = useStoreSlug();

  return (
    <>
      <div className="orderList-dashboard">
        <a href={`/go/${storeSlug}/manage/dashboard`}>Your Dashboard</a>
      </div>
      {order.map((item, index) => {
        const key = String(index);
        return (
          <Grid
            item
            key={key}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            className="orderList-product">
            <TableContainer
              className={`tableContainer ${mobileView ? '-mobile' : ''}`}>
              <Table>
                {tableHeader(data)}
                {tableBody(item)}
              </Table>
            </TableContainer>
          </Grid>
        );
      })}
    </>
  );
};

export default OrderConfirmationList;
